/**
 * HomeDashboard — loginHS 로그인 홈(검색 + 최신 관세 업무 대시보드).
 *   개인 업무(최근 검색·즐겨찾기·사례철)와 최신 변경고시·분류사례·공지를 한 화면에 둔다.
 */
import { useEffect, useState } from "react";
import { buildRows } from "./memoApi";
import { ExternalLink, Sparkles, Clock, Star, FolderOpen, Bell, ShieldAlert, Smartphone } from "lucide-react";
import CenterSearchBar from "./CenterSearchBar";
import { useAuditSearch } from "./AuditSearchContext";
import AmendmentNoticeModal from "../components/AmendmentNoticeModal";
import {
  amendmentNoticeToNewsItem,
  fetchRecentAmendmentNotices,
  openNewsPage,
  useLatestAmendmentNotice,
} from "../components/amendmentNotice";
import { LOGINHS_NOTICES } from "../components/NewsPage";
import { mergeNewsItems } from "../components/newsFeed";

const RECENT_KEY = "loginhs_recent_searches";

function loadRecents() {
  try {
    const a = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(a) ? a.filter((x) => typeof x === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
}

function authHeaders() {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtHsk(s) {
  const d = String(s || "").replace(/\D/g, "");
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
}


// 내 사례철 → 품목분류사례 탐색기 상세 딥링크
function goCase(caseId) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "precedents");
  if (caseId) u.searchParams.set("case", caseId);
  else u.searchParams.delete("case");
  ["sq", "chapter", "section", "page", "hs", "hsk"].forEach((k) =>
    u.searchParams.delete(k),
  );
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// LoginNetwork 로고 가이드(2022-04-12): Main #FED100, Sub Main #1A1A1A, Sub #F1F1F1.
const BRAND_INK = "#1a1a1a";
const BRAND_LINE = "#d1d5db";
const HERO_WASH = "linear-gradient(180deg, rgba(26,26,26,0.025), transparent 24%)";
const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"];
const ICON_TONES = {
  ai: { background: "#EEF2FF", color: "#4F46E5" },
  brand: { background: "#FED100", color: "#1A1A1A" }, // LoginNetwork 브랜드 노랑(로고 문 색) — 홈 인사말
  data: { background: "#ECFDF5", color: "#0F766E" },
  news: { background: "#FFF7ED", color: "#C2410C" },
  recent: { background: "#F1F5F9", color: "#475569" },
  favorite: { background: "#FFFBEB", color: "#B45309" },
  work: { background: "#EFF6FF", color: "#1D4ED8" },
  neutral: { background: "#F3F4F6", color: BRAND_INK },
};

// 개인화 인사말 — 이름(있으면)+요일. 밋밋한 'LoginHS' 타이틀 대체(사용자 요청 2026-07-14).
function greetLine() {
  let name = "";
  try {
    name = (localStorage.getItem("userName") || "").trim();
  } catch {
    name = "";
  }
  const day = WEEKDAY[new Date().getDay()];
  const who = name ? `${name}님, ` : "";
  return `${who}즐거운 ${day}요일이에요`;
}

function IconTile({ tone = "neutral", size = 26, children }) {
  const colors = ICON_TONES[tone] || ICON_TONES.neutral;
  return (
    <span
      className="lhs-home-icon"
      data-icon-tone={tone}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size >= 29 ? 9 : 8,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        color: colors.color,
        background: colors.background,
      }}
    >
      {children}
    </span>
  );
}

function Card({ title, icon, iconTone = "neutral", action, children }) {
  return (
    <div
      className="lhs-home-card"
      style={{
        position: "relative",
        border: "1px solid var(--audit-border)",
        borderRadius: 12,
        background: "var(--audit-bg-elevated, var(--audit-bg))",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        minHeight: 150,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon && (
            <IconTile tone={iconTone}>
              {icon}
            </IconTile>
          )}
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--audit-text)" }}>{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

const EmptyLine = ({ children }) => (
  <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)", padding: "6px 0" }}>{children}</div>
);

function runningAsInstalledApp() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
      || localStorage.getItem("loginhs:in-agent") === "1";
  } catch {
    return false;
  }
}

function goMemo() {
  window.history.pushState({}, "", "/?audit=mymemo");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function HomeDashboard() {
  const { setPendingSearch } = useAuditSearch();
  const [recents, setRecents] = useState(loadRecents);
  const [watch, setWatch] = useState({ loading: true, items: [] });
  const [cases, setCases] = useState({ loading: true, items: [] });
  const [news, setNews] = useState({ loading: true, items: [] });
  const [amendmentNewsItems, setAmendmentNewsItems] = useState([]);
  const [precedents, setPrecedents] = useState({ loading: true, items: [] });
  const [rates, setRates] = useState([]);
  const latestAmendmentNotice = useLatestAmendmentNotice();

  useEffect(() => {
    setRecents(loadRecents());
    let alive = true;
    // mymemo 카드 — 메모 + ★ 세번을 mymemo 화면과 같은 규칙으로 합쳐 최근 순
    Promise.all([
      fetch("/api/onto/memo", { headers: authHeaders() }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
      fetch("/api/onto/watchlist", { headers: authHeaders() }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
    ]).then(([m, w]) => {
      if (!alive) return;
      const rows = buildRows({ memos: m.items || [], stars: Array.isArray(w?.items) ? w.items : [] })
        .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
      setWatch({ loading: false, items: rows });
    });
    fetch("/api/onto/saved-cases", { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!alive) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setCases({ loading: false, items });
      })
      .catch(() => alive && setCases({ loading: false, items: [] }));
    fetch("/api/onto/customs-notices")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!alive) return;
        const items = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : Array.isArray(d?.notices) ? d.notices : [];
        setNews({ loading: false, items: items.slice(0, 6) });
      })
      .catch(() => alive && setNews({ loading: false, items: [] }));
    fetchRecentAmendmentNotices(8)
      .then((items) => {
        if (!alive) return;
        setAmendmentNewsItems(items.map(amendmentNoticeToNewsItem).filter(Boolean));
      })
      .catch(() => {});
    fetch("/api/onto/precedents-recent?source=domestic&limit=6&offset=0")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!alive) return;
        setPrecedents({ loading: false, items: Array.isArray(d?.items) ? d.items : [] });
      })
      .catch(() => alive && setPrecedents({ loading: false, items: [] }));
    fetch("/api/tools/exchange-rate?imexTp=2")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!alive) return;
        const wanted = new Set(["USD", "EUR", "CNY", "JPY"]);
        setRates((Array.isArray(d?.rows) ? d.rows : []).filter((x) => wanted.has(x.curr_sgn)).slice(0, 4));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const search = (q) => setPendingSearch({ q, ts: Date.now(), mode: "auto" });
  const amendmentNews = amendmentNoticeToNewsItem(latestAmendmentNotice);
  const visibleNews = mergeNewsItems(
    amendmentNews ? [amendmentNews] : [],
    amendmentNewsItems,
    LOGINHS_NOTICES,
    news.items,
  ).slice(0, 6);

  return (
    <div style={{ maxWidth: 1160, margin: "0 auto", padding: "5vh 20px 40px", background: HERO_WASH }}>
      <AmendmentNoticeModal notice={latestAmendmentNotice} audience="member" />
      {/* 카드 구조는 중립색으로 유지하고 역할별 색상은 작은 아이콘 타일로 한정한다. */}
      <style>{`
        .lhs-home-card { transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease; }
        .lhs-home-card::before { content:""; position:absolute; top:0; left:0; right:0; height:2px; border-radius:12px 12px 0 0; background:${BRAND_LINE}; opacity:0; transition:opacity .14s ease; }
        .lhs-home-card:hover { transform: translateY(-2px); border-color: rgba(26,26,26,.44); box-shadow: 0 7px 24px -14px rgba(26,26,26,.45); }
        .lhs-home-card:hover::before { opacity:1; }
        .lhs-home-action { color:${BRAND_INK} !important; font-weight:700; }
        .lhs-install-pill:hover { background: #000 !important; }
        .lhs-home-action:hover { text-decoration:underline; text-decoration-color:#9ca3af; text-decoration-thickness:1px; text-underline-offset:3px; }
        @media (max-width: 860px) {
          .lhs-home-work-grid { grid-template-columns: 1fr !important; }
          .lhs-home-precedent-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* 히어로 — 개인화 인사말 + LoginNetwork 브랜드 포인트 */}
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 9, fontSize: 25, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--audit-text)" }}>
          <IconTile tone="brand" size={29}>
            <Sparkles size={18} />
          </IconTile>
          <span>{greetLine()}</span>
        </div>
        <div style={{ fontSize: 13.5, color: "var(--audit-text-muted)", marginTop: 6 }}>
          품명을 검색하면 근거와 함께 세번을 찾아드립니다
        </div>
        {/* 회사 홈페이지(loginhs.co.kr) — 로고=홈(대시보드)과 구분되는 외부 사이트, 새 탭(2026-07-14). */}
        <a
          href="https://www.loginhs.co.kr"
          target="_blank"
          rel="noopener noreferrer"
          title="loginhs.co.kr 홈페이지 (새 탭)"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginTop: 8,
            fontSize: 12.5,
            color: BRAND_INK,
            textDecoration: "none",
          }}
        >
          loginhs.co.kr 홈페이지 <ExternalLink size={13} />
        </a>
        {/* 서비스 소개서(PDF) — AI 생성 브로슈어(2026-07-14). 공유·프린트용. */}
        <a
          href="/api/onto/intro-brochure"
          target="_blank"
          rel="noreferrer"
          title="loginHS 서비스 소개서 (PDF, 새 탭)"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginTop: 8,
            marginLeft: 14,
            fontSize: 12.5,
            color: BRAND_INK,
            textDecoration: "none",
          }}
        >
          서비스 소개서 (PDF) <ExternalLink size={13} />
        </a>
      </div>
      {/* 모바일 앱 설치(2026-10-02) — 눈에 띄게: 어두운 바탕·흰 글자·로고 노랑 아이콘. PC 에서 열면 QR. 이미 설치한 앱에서는 숨김 */}
      {!runningAsInstalledApp() && <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
        <a
          href="/?page=install"
          title="휴대폰 홈 화면에 loginHS 를 앱으로 설치 — PC 에서 열면 휴대폰으로 찍는 QR"
          data-testid="home-install"
          className="lhs-install-pill"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            height: 34,
            padding: "0 16px",
            borderRadius: 999,
            background: "#1A1A1A",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            textDecoration: "none",
            boxShadow: "0 2px 8px rgba(26,26,26,.18)",
          }}
        >
          <Smartphone size={15} color="#FED100" /> {/Windows NT/i.test(navigator.userAgent || "") ? "PC·휴대폰 앱 설치하기" : "휴대폰에 앱 설치하기"}
        </a>
      </div>}

      {/* 검색바 (플로팅 아닌 인라인) */}
      <div style={{ display: "flex", justifyContent: "center", margin: "18px 0 34px" }}>
        <div style={{ width: "min(720px, 96%)" }}>
          <CenterSearchBar variant="home" />
        </div>
      </div>

      {/* 최신 변경고시 — 감시 결과와 동일한 정본 피드. 신규 고시는 대시보드 최상단에 고정한다. */}
      {latestAmendmentNotice && (
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 18,
            alignItems: "center",
            padding: "17px 19px",
            marginBottom: 14,
            border: "1px solid #d7d7d7",
            borderRadius: 12,
            background: "var(--audit-bg-elevated, var(--audit-bg))",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 750, color: "var(--audit-text-muted)" }}>
              <ShieldAlert size={15} color={BRAND_INK} /> 품목분류 변경고시 · {latestAmendmentNotice.gosiDate}
            </div>
            <div style={{ marginTop: 5, fontSize: 16, fontWeight: 800, color: "var(--audit-text)" }}>
              {latestAmendmentNotice.title}
            </div>
            <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.55, color: "var(--audit-text-muted)" }}>
              이번 고시 {Number(latestAmendmentNotice.changedItemCount || 0).toLocaleString()}개 항목 · 검수 대기 {Number(latestAmendmentNotice.pendingReviewCount || 0).toLocaleString()}건 · 변경 이력은 세번 상세에서 원문과 함께 확인
            </div>
          </div>
          <button
            onClick={openNewsPage}
            style={{ minHeight: 36, padding: "0 13px", border: `1px solid ${BRAND_INK}`, borderRadius: 8, background: BRAND_INK, color: "#fff", font: "700 12.5px inherit", cursor: "pointer" }}
          >
            공지 확인 →
          </button>
        </section>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(300px, .65fr)", gap: 14, marginBottom: 14 }} className="lhs-home-work-grid">
        <Card
          title="최신 품목분류사례"
          icon={<FolderOpen size={15} />}
          iconTone="data"
          action={<button className="lhs-home-action" onClick={() => goCase("")} style={{ border: 0, background: "transparent", color: BRAND_INK, fontSize: 11.5, cursor: "pointer" }}>전체 ↗</button>}
        >
          {precedents.loading ? (
            <EmptyLine>불러오는 중…</EmptyLine>
          ) : precedents.items.length === 0 ? (
            <EmptyLine>최신 분류사례가 없습니다.</EmptyLine>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 16 }} className="lhs-home-precedent-grid">
              {precedents.items.map((c, i) => (
                <button key={c.case_id || i} onClick={() => goCase(c.case_id)} style={{ display: "grid", gridTemplateColumns: "96px minmax(0, 1fr) auto", alignItems: "center", gap: 8, minHeight: 33, padding: "4px 0", border: 0, borderBottom: "1px solid var(--audit-border)", background: "transparent", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                  <span style={{ font: "700 11.5px JetBrains Mono, monospace", color: "var(--audit-text)" }}>{fmtHsk(c.hsk || "") || "—"}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, color: "var(--audit-text)" }}>{c.title || "(품명 없음)"}</span>
                  <span style={{ fontSize: 10.5, color: "var(--audit-text-muted)" }}>{c.decision_date || ""}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card title="관세 업무 새소식" icon={<Bell size={15} />} iconTone="news" action={<button className="lhs-home-action" onClick={openNewsPage} style={{ border: 0, background: "transparent", color: BRAND_INK, fontSize: 11.5, cursor: "pointer" }}>더보기 ↗</button>}>
          {news.loading && !amendmentNews ? (
            <EmptyLine>불러오는 중…</EmptyLine>
          ) : visibleNews.length === 0 ? (
            <EmptyLine>새소식이 없습니다.</EmptyLine>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {visibleNews.slice(0, 5).map((n, i) => (
                <button key={n.id || i} onClick={openNewsPage} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 8, padding: "2px 0", border: 0, background: "transparent", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, color: "var(--audit-text)" }}>{n.title || n.subject || n.name}</span>
                  <span style={{ fontSize: 10.5, color: "var(--audit-text-muted)" }}>{String(n.date || n.reg_date || "").slice(0, 10)}</span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={{ margin: "22px 0 10px", fontSize: 13, fontWeight: 800, color: "var(--audit-text)" }}>내 업무</div>

      {/* 개인 업무 카드 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 14,
        }}
      >
        {/* 최근 검색 */}
        <Card title="최근 검색" icon={<Clock size={15} />} iconTone="recent">
          {recents.length === 0 ? (
            <EmptyLine>최근 검색이 없습니다.</EmptyLine>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {recents.map((r, i) => (
                <button
                  key={i}
                  onClick={() => search(r)}
                  style={{
                    border: "1px solid var(--audit-border)",
                    background: "transparent",
                    borderRadius: 999,
                    padding: "4px 12px",
                    fontSize: 12.5,
                    cursor: "pointer",
                    color: "var(--audit-text)",
                    fontFamily: "inherit",
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* mymemo — 업무 메모·세번 메모·★ 세번(2026-10-02, 세번 즐겨찾기 카드를 대신) */}
        <Card
          title="mymemo"
          icon={<Star size={15} />}
          iconTone="favorite"
          action={
            <button
              onClick={goMemo}
              className="lhs-home-action"
              style={{ border: "none", background: "transparent", color: BRAND_INK, fontSize: 11.5, cursor: "pointer" }}
              title="mymemo 열기"
            >
              전체 ↗
            </button>
          }
        >
          {watch.loading ? (
            <EmptyLine>불러오는 중…</EmptyLine>
          ) : watch.items.length === 0 ? (
            <EmptyLine>아직 메모가 없습니다. 업무 메모를 적거나 세번 상세에서 ★ 를 누르면 여기에 모입니다.</EmptyLine>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {watch.items.slice(0, 6).map((r) => (
                <button
                  key={r.key}
                  onClick={goMemo}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: "3px 0",
                    textAlign: "left",
                    fontFamily: "inherit",
                    minWidth: 0,
                  }}
                >
                  <span style={{ fontFamily: "Consolas, monospace", fontSize: 12, color: BRAND_INK, fontWeight: 700, flexShrink: 0 }}>
                    {r.hsk ? fmtHsk(r.hsk) : "업무"}
                  </span>
                  <span style={{ fontSize: 12.5, color: "var(--audit-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.title || "★"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* 내 사례철 */}
        <Card
          title="내 사례철"
          icon={<FolderOpen size={15} />}
          iconTone="data"
          action={
            <button
              onClick={() => {
                const u = new URL(window.location.href);
                u.searchParams.set("audit", "precedents");
                u.searchParams.delete("case");
                window.history.pushState({}, "", u.toString());
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
              className="lhs-home-action"
              style={{ border: "none", background: "transparent", color: BRAND_INK, fontSize: 11.5, cursor: "pointer" }}
              title="품목분류사례에서 관리"
            >
              전체 ↗
            </button>
          }
        >
          {cases.loading ? (
            <EmptyLine>불러오는 중…</EmptyLine>
          ) : cases.items.length === 0 ? (
            <EmptyLine>담은 사례가 없습니다. 사례 상세에서 ☆로 담으세요.</EmptyLine>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {cases.items.slice(0, 6).map((c, i) => (
                <button
                  key={c.case_id || i}
                  onClick={() => goCase(c.case_id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: "3px 0",
                    textAlign: "left",
                    fontFamily: "inherit",
                  }}
                >
                  <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12, color: BRAND_INK, fontWeight: 700, flexShrink: 0 }}>
                    {fmtHsk(c.hsk || "") || "—"}
                  </span>
                  <span style={{ fontSize: 12.5, color: "var(--audit-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.title || "(품명 없음)"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* 관세 업무 바로가기 + 주간 과세환율 — CLHS형 정보의 현대적 요약. */}
        <Card title="실무 바로가기" icon={<ExternalLink size={15} />} iconTone="work">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
            {[
              ["관세율표", "?audit=quickref&noextra=1"],
              ["품목분류사례", "?audit=precedents"],
              ["관세환율", "?page=exchange"],
              ["세금 계산기", "?page=taxcalc"],
            ].map(([label, href]) => (
              <a key={label} href={`/${href}`} style={{ padding: "7px 9px", border: "1px solid var(--audit-border)", borderRadius: 7, color: "var(--audit-text)", fontSize: 12.5, textDecoration: "none" }}>{label} →</a>
            ))}
          </div>
          {rates.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 5, marginTop: 10 }}>
              {rates.map((r) => (
                <div key={r.curr_sgn} style={{ padding: "6px 7px", borderRadius: 6, background: "var(--audit-bg-muted, rgba(148,163,184,.08))" }}>
                  <div style={{ fontSize: 10.5, color: "var(--audit-text-muted)" }}>{r.curr_sgn}</div>
                  <div style={{ marginTop: 1, font: "700 11.5px JetBrains Mono, monospace", color: "var(--audit-text)" }}>{Number(r.fxrt).toLocaleString("ko-KR", { maximumFractionDigits: 2 })}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}
